/**
 * The app's history: where the tab is, how to move it, and what may stop it.
 *
 * One module-level store rather than a router library or a React context, so
 * any component — or a sibling of `App`, like the cookie notice — can read the
 * location and move it, and a component test needs no provider around it.
 *
 * Every entry the store touches carries `history.state = { idx, from }`:
 *
 * - `idx` is the entry's position in the tab's history. A Back press is a
 *   `popstate` that has already happened by the time anyone hears of it, so
 *   refusing one means travelling back to where the screen still is — and
 *   that takes knowing how far away it was.
 * - `from` is the path the entry was pushed from. It is how "close" can mean
 *   Back when the screen underneath is the one being returned to, so that
 *   Back → close → Back does not reopen what was just closed.
 * - `depth`, present only above zero, counts the steps taken inside one
 *   screen since it was entered — the sections of the strategy being edited.
 *   Such an entry keeps the `from` of the entry that entered the screen, so
 *   closing the screen goes back past every step rather than into the last.
 *
 * All three survive a reload, because the browser keeps an entry's state with it.
 */

import { useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import { parseRoute, type AppRoute } from "./routes";

export interface AppLocation {
    pathname: string;
    search: string;
    /** This entry's position in the tab's history. */
    idx: number;
    /** The path this entry was pushed from; null for one the store did not push. */
    from: string | null;
    /** The steps taken inside the screen since it was entered — see the module note. */
    depth: number;
}

export type PendingNavigation =
    | { kind: "navigate"; to: string; replace: boolean; within: boolean }
    | { kind: "traverse"; delta: number }
    | { kind: "action"; run: () => void };

interface EntryStamp {
    idx: number;
    from: string | null;
    depth: number;
}

/**
 * Whether a guarded screen would lose its work by a move to `destination` —
 * the path and query the move goes to, or null where it goes nowhere the
 * screen could recognise: a reload, a closed tab, a property switch.
 */
export type NavigationBlockCheck = (destination: string | null) => boolean;

let snapshot: AppLocation | null = null;
const locationListeners = new Set<() => void>();

const blockers = new Set<{ current: NavigationBlockCheck }>();
let pending: PendingNavigation | null = null;
const pendingListeners = new Set<() => void>();

/**
 * Set once a discard has been confirmed, so the navigation that discard was
 * for — and anything it sets off before the guarded screen goes away — is not
 * asked about the same edits a second time.
 */
let disarmed = false;
/** While a refused Back is being undone: the entry the screen is still on. */
let revertingToIdx: number | null = null;
/** While a confirmed Back is under way: the entry it is travelling to. */
let confirmedTraverseIdx: number | null = null;

let listenersAttached = false;

function readStamp(): EntryStamp | null {
    const state: unknown = window.history.state;
    if (typeof state !== "object" || state === null) return null;
    const { idx, from, depth } = state as { idx?: unknown; from?: unknown; depth?: unknown };
    if (typeof idx !== "number" || !Number.isFinite(idx)) return null;
    return {
        idx,
        from: typeof from === "string" ? from : null,
        depth: typeof depth === "number" && Number.isInteger(depth) && depth > 0 ? depth : 0,
    };
}

/** The state an entry carries: `depth` only where there is one, so an ordinary entry reads as it always has. */
function entryState(stamp: EntryStamp): { idx: number; from: string | null; depth?: number } {
    return stamp.depth > 0
        ? { idx: stamp.idx, from: stamp.from, depth: stamp.depth }
        : { idx: stamp.idx, from: stamp.from };
}

function stampCurrentEntry(stamp: EntryStamp): void {
    const state: unknown = window.history.state;
    const base = typeof state === "object" && state !== null ? state : {};
    window.history.replaceState({ ...base, ...entryState(stamp) }, "");
}

/**
 * The current location, rebuilt only when the address actually moved — the
 * same object otherwise, as `useSyncExternalStore` requires. Reading the
 * address rather than trusting the last write is what lets a location changed
 * outside the store (a test, or an entry from before this build) be picked up
 * rather than missed; an entry nobody stamped is stamped here.
 */
export function getLocation(): AppLocation {
    // Mid-undo the address bar briefly shows the entry being refused, and the
    // screen must not follow it there.
    if (revertingToIdx !== null && snapshot) return snapshot;

    const { pathname, search } = window.location;
    const stamp = readStamp();
    if (
        snapshot
        && stamp
        && snapshot.pathname === pathname
        && snapshot.search === search
        && snapshot.idx === stamp.idx
        && snapshot.from === stamp.from
        && snapshot.depth === stamp.depth
    ) {
        return snapshot;
    }

    let resolved = stamp;
    if (!resolved) {
        resolved = { idx: snapshot ? snapshot.idx + 1 : 0, from: null, depth: 0 };
        stampCurrentEntry(resolved);
    }
    snapshot = { pathname, search, idx: resolved.idx, from: resolved.from, depth: resolved.depth };
    return snapshot;
}

function emitLocation(): void {
    for (const listener of [...locationListeners]) listener();
}

function setPending(next: PendingNavigation | null): void {
    pending = next;
    for (const listener of [...pendingListeners]) listener();
}

function isBlocked(destination: string | null): boolean {
    if (disarmed) return false;
    for (const blocker of blockers) {
        if (blocker.current(destination)) return true;
    }
    return false;
}

function onPopState(): void {
    const previous = snapshot;
    const stamp = readStamp();

    if (revertingToIdx !== null) {
        if (stamp && stamp.idx === revertingToIdx) {
            // Our own undo has landed; the screen never left.
            revertingToIdx = null;
            emitLocation();
        } else if (stamp && previous) {
            // Back again before the first undo landed: undo that one as well.
            window.history.go(previous.idx - stamp.idx);
            setPending({ kind: "traverse", delta: stamp.idx - previous.idx });
        }
        return;
    }

    if (confirmedTraverseIdx !== null && stamp && stamp.idx === confirmedTraverseIdx) {
        confirmedTraverseIdx = null;
        disarmed = false;
        emitLocation();
        return;
    }

    if (!stamp || !previous) {
        // An entry this store never stamped, from before this build: adopt it.
        emitLocation();
        return;
    }

    if (stamp.idx === previous.idx) {
        // Nothing moved — the load-time popstate older browsers fire.
        if (window.location.pathname !== previous.pathname || window.location.search !== previous.search) {
            emitLocation();
        }
        return;
    }

    // The address already shows where the traversal went.
    if (isBlocked(window.location.pathname + window.location.search)) {
        revertingToIdx = previous.idx;
        window.history.go(previous.idx - stamp.idx);
        setPending({ kind: "traverse", delta: stamp.idx - previous.idx });
        return;
    }

    emitLocation();
}

function onBeforeUnload(event: BeforeUnloadEvent): void {
    if (!isBlocked(null)) return;
    event.preventDefault();
    // Older browsers show the prompt only when this is set as well.
    event.returnValue = "";
}

function attachListeners(): void {
    if (listenersAttached) return;
    listenersAttached = true;
    window.addEventListener("popstate", onPopState);
    window.addEventListener("beforeunload", onBeforeUnload);
}

function detachListenersWhenIdle(): void {
    if (!listenersAttached || locationListeners.size > 0 || blockers.size > 0) return;
    listenersAttached = false;
    window.removeEventListener("popstate", onPopState);
    window.removeEventListener("beforeunload", onBeforeUnload);
}

function subscribeLocation(listener: () => void): () => void {
    locationListeners.add(listener);
    attachListeners();
    return () => {
        locationListeners.delete(listener);
        detachListenersWhenIdle();
    };
}

function subscribePending(listener: () => void): () => void {
    pendingListeners.add(listener);
    return () => {
        pendingListeners.delete(listener);
    };
}

function commitNavigation(to: string, replace: boolean, within: boolean): void {
    const current = getLocation();
    if (replace) {
        window.history.replaceState(entryState(current), "", to);
    } else if (within) {
        window.history.pushState(
            entryState({ idx: current.idx + 1, from: current.from, depth: current.depth + 1 }),
            "",
            to,
        );
    } else {
        window.history.pushState(
            { idx: current.idx + 1, from: current.pathname + current.search },
            "",
            to,
        );
    }
    disarmed = false;
    emitLocation();
}

export interface NavigateOptions {
    /** Rewrite this entry instead of adding one. */
    replace?: boolean;
    /**
     * The move keeps the user on the guarded screen — a save that gives what
     * is open a new address — so there is nothing to ask about discarding.
     */
    ignoreBlockers?: boolean;
    /**
     * The move is a step inside the screen this entry belongs to — one
     * section of the open strategy to another. Back still walks it, but
     * leaving the screen (`navigateUp`) goes back past every such step.
     */
    within?: boolean;
}

/**
 * Move the tab to `to`: a new entry, or with `replace` a rewrite of this one.
 * A guarded screen with something to lose holds the move until the discard is
 * confirmed. Moving to the page already on screen does nothing, so clicking
 * the rail item that is already active does not grow the history.
 */
export function navigate(to: string, options: NavigateOptions = {}): void {
    const current = getLocation();
    if (to === current.pathname + current.search) return;
    const replace = options.replace === true;
    const within = options.within === true;
    if (options.ignoreBlockers !== true && isBlocked(to)) {
        setPending({ kind: "navigate", to, replace, within });
        return;
    }
    commitNavigation(to, replace, within);
}

/**
 * Leave for the screen above this one. When this entry was pushed from that
 * very screen, that is a Back — so the history reads as the user walked it,
 * and Back afterwards does not reopen what was closed. Steps taken inside
 * the screen (`within`) are gone back past with it. Otherwise (the screen was
 * reached directly, or from elsewhere) the parent replaces this entry, or is
 * pushed, as the caller decides.
 */
export function navigateUp(parent: string, options: { fallback: "replace" | "push" }): void {
    const current = getLocation();
    if (current.from === parent) {
        if (current.depth > 0) {
            window.history.go(-(current.depth + 1));
        } else {
            window.history.back();
        }
        return;
    }
    navigate(parent, { replace: options.fallback === "replace" });
}

/** Back to wherever the reader came from in this app, or to `fallback` if they came from outside it. */
export function goBack(fallback: string): void {
    const current = getLocation();
    if (current.from !== null) {
        window.history.back();
        return;
    }
    navigate(fallback);
}

/**
 * Whether a guarded screen holds work a move would lose. Asked when the tab
 * turns out to show a property that is no longer open: such a screen is then
 * hidden rather than emptied, so its work is still there if the property comes
 * back and the guard still asks before a reload throws it away (`App.tsx`).
 * Like the guard, it asks the screen at the moment, never on render.
 */
export function hasUnsavedWork(): boolean {
    return isBlocked(null);
}

/** Run an action that would lose a guarded screen's edits — a property switch — only once that is confirmed. */
export function runWhenUnblocked(action: () => void): void {
    if (isBlocked(null)) {
        setPending({ kind: "action", run: action });
        return;
    }
    action();
}

export function getPendingNavigation(): PendingNavigation | null {
    return pending;
}

/** The user chose to discard: carry out whatever was held. */
export function confirmPendingNavigation(): void {
    const held = pending;
    if (!held) return;
    setPending(null);
    if (held.kind === "navigate") {
        commitNavigation(held.to, held.replace, held.within);
    } else if (held.kind === "traverse") {
        confirmedTraverseIdx = getLocation().idx + held.delta;
        disarmed = true;
        window.history.go(held.delta);
    } else {
        disarmed = true;
        held.run();
    }
}

/** The user chose to stay. A refused Back has already been undone. */
export function cancelPendingNavigation(): void {
    if (pending) setPending(null);
}

export function useLocation(): AppLocation {
    return useSyncExternalStore(subscribeLocation, getLocation);
}

export function useRoute(): AppRoute | null {
    const { pathname, search } = useLocation();
    return useMemo(() => parseRoute(pathname, search), [pathname, search]);
}

export function usePendingNavigation(): PendingNavigation | null {
    return useSyncExternalStore(subscribePending, getPendingNavigation);
}

/**
 * Guard a screen that can hold unsaved work. `shouldBlock` is asked at the
 * moment something would leave — never on render — so a costly comparison
 * (the strategy editor compares whole parameter sets) is paid once per
 * attempt rather than once per keystroke. It is told where the move goes, so
 * a move that stays on the screen — another section of the strategy being
 * edited — can pass without a question. It also stands between the tab and a
 * reload or close, which the browser asks about in its own words, and which go
 * nowhere the screen could recognise (`null`).
 */
export function useNavigationBlocker(shouldBlock: NavigationBlockCheck): void {
    const latest = useRef(shouldBlock);
    latest.current = shouldBlock;

    useEffect(() => {
        const blocker = { current: (destination: string | null) => latest.current(destination) };
        blockers.add(blocker);
        disarmed = false;
        attachListeners();
        return () => {
            blockers.delete(blocker);
            disarmed = false;
            detachListenersWhenIdle();
        };
    }, []);
}
